/*
    Every user-facing string, in English and Turkish.

    English is the source of truth: its keys define the type, so a missing or
    misspelt Turkish key is a compile error rather than a blank label someone
    notices in production. `{name}` placeholders are filled by t().
*/

export const en = {
    'app.settings': 'Settings',
    'nav.mail': 'Mail',
    'nav.folders': 'Folders',
    'app.backToMail': 'back to mail',
    'app.loading': 'loading',
    'app.cancel': 'Cancel',
    'app.save': 'Save',
    'app.delete': 'Delete',
    'app.create': 'Create',
    'app.rename': 'Rename',
    'app.close': 'Close',
    'app.retry': 'Retry',
    'app.on': 'on',
    'app.off': 'Off',

    'login.addMailbox': 'add a mailbox',
    'login.intro':
        'Each mailbox signs in separately — they are independent accounts on the server.',
    'login.address': 'Email address',
    'login.password': 'Password',
    'login.signInTo': 'Sign in to your mailbox',
    'login.passwordNote':
        'Your password only ever goes to your own mail infrastructure — the mail, calendar/contacts and management services. It goes nowhere else, it is never written down, and it is erased when you sign out.',
    'login.remember': 'Remember me',
    'login.rememberNote': 'Keeps you signed in on this device for a week. Leave it off on a shared computer.',
    'login.submit': 'Sign in',
    'login.working': 'Signing in…',
    'login.rejected': 'That address and password were not accepted.',
    'login.sessionEnded': 'Your session has ended. Sign in again to carry on.',
    'session.ended': 'The session for {address} has ended.',
    'login.tooMany': 'Too many sign-in attempts. Wait a few minutes and try again.',
    'login.failed': 'Could not reach the mail server. Try again in a moment.',
    'login.needBoth': 'Enter both your address and your password.',
    'login.added': 'added',

    'folders.role.inbox': 'Inbox',
    'folders.role.drafts': 'Drafts',
    'folders.role.sent': 'Sent',
    'folders.role.archive': 'Archive',
    'folders.role.junk': 'Spam',
    'folders.role.trash': 'Trash',
    'account.switchOrAdd': 'switch or add',
    'account.yours': 'your mailbox',
    'brand.operatorFor': 'for {name}',
    'account.count': '{count} mailboxes',
    'folders.title': 'folders',
    'folders.new': 'New folder',
    'folders.actions': 'Folder actions',
    'folders.newSub': 'New subfolder…',
    'folders.rename': 'Rename…',
    'folders.deleteDots': 'Delete…',
    'folders.hide': 'Hide from this list',
    'folders.show': 'Show in this list',
    'folders.toTop': 'Move to top level',
    'folders.name': 'Folder name',
    'folders.newIn': 'New folder in {name}',
    'folders.renaming': 'Rename {name}',
    'folders.deleting': 'Delete {name}',
    'folders.deleteEmpty': 'The folder is empty. This cannot be undone.',
    'folders.deleteFull':
        'This folder holds {count} messages, and deleting it deletes them too. There is no undo. Type {name} to confirm.',
    'folders.empty': 'Empty…',
    'folders.emptying': 'Empty {name}?',
    'folders.emptyBody': 'The {count} messages in {name} will be deleted for good. This cannot be undone.',
    'folders.emptyConfirm': 'Empty',
    'folders.emptied': '{name} emptied — {count} messages deleted for good',
    'folders.hiddenCount': '{count} hidden',
    'folders.hiddenOne': '1 hidden folder',
    'folders.hiddenMany': '{count} hidden folders',
    'folders.showHidden': 'show hidden folders',
    'folders.hideHidden': 'hide them again',
    'folders.noRole':
        '{names} has no role set on the server, so other mail apps don’t know what it is.',
    'folders.noRoleMany':
        '{names} have no role set on the server, so other mail apps don’t know what they are.',
    'folders.fixRoles': 'Fix for every app',
    'folders.fixing': 'Fixing…',
    'folders.moveTo': 'Move to…',
    'folders.createNamed': 'Create “{name}”',
    'folders.noMatch': 'No folder matches.',

    'list.messages': '{count} messages',
    'list.conversations': '{count} conversations',
    'list.unread': '{count} unread',
    'list.empty': 'Nothing in this folder.',
    'list.emptyFolder': 'Empty this folder',
    'list.noSubject': '(no subject)',
    'list.searchPlaceholder': 'Search — prefix all: to search everything',
    'list.searchTitle': 'Search',
    'list.searching': 'searching…',
    'list.results': '{count} results',
    'list.resultsOne': '1 result',
    'list.inAllMail': 'all mail',
    'list.inThisFolder': 'this folder',
    'list.clear': 'Clear (esc)',
    'list.draftTo': 'To {name}',
    /* Screen-reader only. The list conveys unread, attachment, flag and draft with weight,
       a dot and two icons — none of which reach a person who is listening to the row. */
    'keys.action.send': 'Send',
    'keys.action.selectAll': 'Select everything loaded',
    'keys.action.search': 'Search',
    'keys.scope.compose': 'writing',
    'list.aria': 'Message list',
    'list.rowSelect': 'Select this message',
    'list.stUnread': 'Unread',
    'list.stFlagged': 'Starred',
    'list.stAttachment': 'Has attachment',
    'list.stDraft': 'Draft',
    'list.stThread': 'Conversation of {count} messages',

    'reader.nothing': 'nothing selected',
    'reader.pick': 'Pick a message.',
    'reader.noBody': '(no readable body)',
    'reader.threadFailed': 'This message could not be loaded.',
    'reader.openMessage': 'Open message',
    'reader.to': 'to {names}',
    'reader.from': 'from {names}',
    'reader.back': 'Back to {name}',
    'reader.blocked': '{count} remote images blocked to protect your privacy.',
    'reader.blockedOne': '1 remote image blocked to protect your privacy.',
    'reader.blockedWhy': 'senders can use these to tell when you opened this',
    'reader.loadImages': 'Load images',
    'reader.attachments': '{count} attachments',
    'reader.attachmentsOne': '1 attachment',
    'reader.unnamed': '(unnamed)',
    'reader.thisMessage': 'this message',
    'reader.spoofTitle': 'This message may not be from the address it shows.',
    'reader.spoofBody': 'Before clicking a link or making a payment, check with the sender another way.',
    'reader.historyOne': '1 earlier message in this conversation',
    'reader.history': '{count} earlier messages in this conversation',

    'action.reply': 'Reply',
    'action.replyAll': 'Reply all',
    'action.forward': 'Forward',
    'action.archive': 'Archive',
    'action.move': 'Move to folder',
    'action.trash': 'Delete',
    'action.star': 'Star',
    'action.markRead': 'Mark as read',
    'action.markUnread': 'Mark as unread',
    'action.compose': 'Compose',
    'action.newMessage': 'New message',
    'action.refresh': 'Refresh',
    'action.undo': 'Undo',
    'toast.movedTo': 'Moved to {folder}',
    'toast.trashed': 'Moved to Trash',
    'toast.erased': 'Deleted for good',
    'toast.archived': 'Archived',
    'toast.movedCount': '{count} messages moved to {folder}',
    'compose.sessionLost': 'Your session has ended, so this cannot be saved to the server right now. What you have written is still here — sign in again in another tab, then close this window to keep it.',
    'action.next': 'Next message',
    'action.prev': 'Previous message',

    'compose.to': 'to',
    'compose.cc': 'cc',
    'compose.bcc': 'bcc',
    'compose.ccBcc': 'cc/bcc',
    'compose.subject': 'subject',
    'compose.send': 'Send',
    'compose.sending': 'Sending…',
    'compose.attach': 'Attach',
    'compose.uploading': 'uploading…',
    'compose.discard': 'Discard',
    'compose.saveQuestion': 'Keep this message as a draft?',
    'compose.saveFailed': 'The draft could not be saved. What you have written is still here — try again in a moment.',
    'compose.saveDraft': 'Save draft',
    'compose.dontSave': "Don't save",
    'compose.keepWriting': 'Keep writing',
    'compose.unsaved': 'unsaved',
    'compose.dropHere': 'Drop to attach',
    'compose.undo': 'Undo ({seconds})',
    'compose.sendNow': 'Send now',
    'compose.later': 'Later',
    'compose.schedule': 'Schedule send',
    'compose.scheduleSend': 'Send at this time',
    'compose.schedulePast': 'That time has already passed.',
    'compose.scheduleTooFar': 'The server holds a message for at most 30 days.',
    'compose.tomorrowMorning': 'Tomorrow morning',
    'compose.thisEvening': 'This evening',
    'compose.mondayMorning': 'Monday morning',
    'compose.failed': 'failed',
    'compose.badAddress': 'Not a valid address: {list}',
    'compose.plainText': 'plain text',
    'compose.contactChip': 'contact',

    'rich.bold': 'Bold',
    'rich.italic': 'Italic',
    'rich.underline': 'Underline',
    'rich.strike': 'Strikethrough',
    'rich.bullets': 'Bulleted list',
    'rich.numbers': 'Numbered list',
    'rich.quote': 'Quote',
    'rich.link': 'Link',
    'rich.image': 'Insert image',
    'rich.clear': 'Clear formatting',
    'rich.apply': 'Apply',
    'rich.placeholder.signature': 'Write your signature here…',
    'rich.placeholder.vacation': 'Write the reply people will get…',
    'rich.placeholder.compose': 'Write your message…',

    'identity.save': 'Save',
    'identity.saving': 'Saving…',
    'identity.saved': 'saved',
    'identity.displayName': 'Display name',
    'identity.signature': 'Signature',
    'identity.plainSignature': 'Plain-text signature',
    'identity.editPlain': 'Plain text version',
    'identity.hidePlain': 'Hide plain text',
    'identity.logoHint': 'A picture you add here travels with the message as part of it, so it shows even in clients that block remote images.',
    'identity.plainHint': 'Sent to anyone reading without HTML. It follows the signature above until you change it here.',
    'identity.mailbox': 'Mailbox',
    'identity.localHint':
        'Your name and signature are kept in your mailbox, in a folder called summail, so they follow you to every computer you sign in from.',
    'identity.noAccount': 'Sign in to a mailbox first.',
    'compose.richText': 'formatted',
    'compose.fullScreen': 'Full screen',
    'compose.dock': 'Dock',
    'compose.closeKeepsDraft': 'Close — keeps a draft (esc)',
    'compose.loadingIdentity': 'loading identity',
    'compose.from': 'from',


    'search.ignored': 'This server can’t search on: {list}. The rest of the search still applied.',
    'sort.title': 'Sort',
    'sort.receivedAt': 'Received',
    'sort.sentAt': 'Sent',
    'sort.from': 'Sender',
    'sort.to': 'Recipient',
    'sort.subject': 'Subject',
    'sort.size': 'Size',

    'bulk.selected': '{count} selected',
    'bulk.selectLoaded': 'Select all {count} loaded',
    'bulk.clear': 'Clear',

    'storage.of': '{used} of {limit}',

    'reader.showQuoted': 'Show trimmed content',
    'reader.hideQuoted': 'Hide trimmed content',
    'reader.print': 'Print',
    'reader.download': 'Download .eml',
    'reader.unsubscribe': 'Unsubscribe',
    'reader.unsubscribeSent': 'Unsubscribe request sent to {to}.',
    'preview.save': 'Save',
    'attachment.download': 'Download',
    'preview.previous': 'Previous attachment',
    'preview.next': 'Next attachment',
    'preview.open': 'Preview',
    'preview.converting': 'preparing this document…',
    'preview.converted': 'converted',
    'preview.convertedWhy': 'Converted to PDF so it can be shown here. Save gives you the original file.',
    'reader.spam': 'Mark as spam',
    'reader.notSpam': 'Not spam',

    'directory.title': 'Directory',
    'directory.link': 'Directory',
    'directory.search': 'Search people…',
    'directory.mailboxes': '{count} mailboxes on this server',
    'directory.none': 'Nobody matches that.',
    'directory.copy': 'Copy address',
    'directory.copied': 'copied',
    'directory.managingTitle': 'Creating and removing mailboxes',
    'directory.managingBody': 'This mail server offers no way to create, rename or delete an account over JMAP, so it is done in the server’s own admin interface rather than here.',
    'directory.permissionMismatch': 'The server does list account-management permissions for you, but not the method that would use them.',
    'directory.openAdmin': 'Open the mail server admin',
    'directory.setAdminUrl': 'Set “adminUrl” in config.json to link straight to it.',
    'directory.privacy': 'Every signed-in user can read this list — names and addresses of every mailbox on the server. That is the mail server’s behaviour, not a setting here, so a shared server is not private between tenants.',
    'contacts.title': 'Contacts',
    'contacts.search': 'Search contacts',
    'contacts.new': 'New contact',
    'contacts.count': '{count} contacts',
    'contacts.import': 'Import vCard',
    'contacts.export': 'Export all',
    'contacts.imported': 'Added {count} contacts.',
    'contacts.saved': 'Saved.',
    'contacts.delete': 'Delete contact',
    'contacts.pick': 'Pick someone on the left, or start a new one.',
    'contacts.empty': 'No contacts yet. Add one here, or from the phone that syncs with this address book.',
    'contacts.name': 'Name',
    'contacts.addEmail': 'Add an address',
    'contacts.addPhone': 'Add a number',
    'contacts.removeLine': 'Remove this line',
    'contacts.write': 'Write',
    'contacts.writeTo': 'Write to {address}',
    'contacts.book': 'Address book',
    'contacts.myBook': 'My contacts',
    'contacts.sharedBook': '{domain} directory · shared',
    'contacts.syncNote': 'These sync with the address book on your phone.',
    'contacts.shared': 'shared',
    'contacts.sharedNotice':
        'This entry is in the address book shared across {domain}. What is changed here changes for everyone with an address there.',
    'contacts.unsavedQuestion': 'Save the changes to this contact first?',
    'contacts.keepEditing': 'Keep editing',
    'contacts.deleted': '{name} deleted',
    'contacts.none': 'This installation has no address book.',
    'contacts.needSomething': 'A contact needs a name or an address.',
    'contacts.noMatches': 'Nobody here matches that.',

    'calendar.title': 'Calendar',
    'calendar.today': 'Today',
    'calendar.previous': 'Previous month',
    'calendar.next': 'Next month',
    'calendar.newEvent': 'New event',
    'calendar.noEvents': 'Nothing on this day.',
    'calendar.name': 'What',
    'calendar.allDay': 'All day',
    'calendar.starts': 'starts',
    'calendar.ends': 'ends',
    'calendar.where': 'Where',
    'calendar.note': 'Notes',
    'calendar.which': 'Calendar',
    'calendar.shared': 'shared',
    'calendar.delete': 'Delete event',
    'calendar.saved': 'saved',
    'calendar.deleted': '{name} deleted',
    'calendar.none': 'This installation has no calendar.',
    'calendar.keepEditing': 'Keep editing',
    'calendar.unsavedQuestion': 'Save the changes to this event first?',
    'calendar.mine': 'My calendar',
    'calendar.theirs': '{domain} calendar',
    'calendar.sharedBadge': 'Shared',
    'calendar.sharedNotice':
        'This event is in the calendar shared across {domain}. What is changed here changes for everyone with an address there.',
    'calendar.recurringNotice':
        'This event repeats. Its title, place and notes can be changed here and the change applies to every occurrence; its times belong to the repeat and are changed where it was created.',

    'contacts.given': 'First name',
    'contacts.surname': 'Last name',
    'contacts.emails': 'email',
    'contacts.phones': 'phone',
    'contacts.addresses': 'address',
    'contacts.addressHint': 'Street, city, country',
    'contacts.organization': 'Company',
    'contacts.jobTitle': 'Job title',
    'contacts.note': 'Notes',
    'contacts.photo': 'Change picture',
    'contacts.add': 'Add to contacts',
    'contacts.added': '{name} is in your contacts.',

    'forwarding.title': 'Forward a copy to',
    'forwarding.on': 'forwarding on',
    'forwarding.add': 'Add',
    'forwarding.saved': 'Saved on the server.',
    'forwarding.originalTitle': 'the copy that stays here',
    'forwarding.keep': 'Keep in Inbox',
    'forwarding.archive': 'Move to a folder',
    'forwarding.delete': 'Delete it',
    'forwarding.into': 'into',
    'forwarding.hint': 'Forwarding runs on the server as mail arrives, before your rules, and it does not rewrite the From or To headers — the message still shows who originally sent it and where it was addressed.',
    'forwarding.off': 'forwarding off',
    'forwarding.turnOff': 'Turn off',
    'forwarding.remove': 'Remove',
    'forwarding.foreign': 'This mailbox already has a filter script that was not written here — most likely set up by hand. It is shown below and left alone: changing it from here could stop mail that somebody is relying on.',
    'forwarding.foreignScript': 'the script on the server',
    'kurulum.ac': 'How do I set this up on my devices?',
    'kurulum.kapat': 'Hide the setup guide',

    'kurulum.otoBaslik': 'The easy way — it configures itself',
    'kurulum.otoMetin': 'Thunderbird and our own summail app find the server settings for you. You will not need anything from the table at the bottom of this page.',
    'kurulum.oto1': 'Open the app and choose to add an account.',
    'kurulum.oto2': 'Type your e-mail address and your password. Nothing else.',
    'kurulum.oto3': 'The app finds the rest on its own and finishes. If it asks you to choose, pick IMAP.',

    'kurulum.androidBaslik': 'Android — our own app',
    'kurulum.androidMetin': 'summail keeps your mail, your contacts and your calendar together on the phone, and sets itself up from your address alone.',
    'kurulum.android1': 'Tap the button below to open summail on Google Play, then install it.',
    'kurulum.android2': 'Open the app and type your e-mail address and password.',
    'kurulum.android3': 'That is all. Your mail arrives, and your contacts and calendar begin to sync.',
    'kurulum.androidDugme': 'Open summail on Google Play',

    'kurulum.outlookBaslik': 'Outlook on Windows (Microsoft 365 / 2021)',
    'kurulum.outlook1': 'Open Outlook and click the File tab at the top left.',
    'kurulum.outlook2': 'Click “Add Account”.',
    'kurulum.outlook3': 'Type your full e-mail address, then click “Advanced options” and tick “Let me set up my account manually”. Click Connect.',
    'kurulum.outlook4': 'From the list of account types, choose IMAP.',
    'kurulum.outlook5': 'Fill in the incoming and outgoing servers from the table at the bottom of this page. Set the encryption method to SSL/TLS in BOTH halves — never “None”.',
    'kurulum.outlook6': 'Type your password and click Connect. Outlook will test the connection and then say it is done.',
    'kurulum.outlookNot': 'Outlook does not read our automatic settings, which is why this one is filled in by hand. In some versions the account type is asked straight away instead of under “Advanced options” — choose IMAP there and carry on the same way.',

    'kurulum.iphoneBaslik': 'iPhone and iPad',
    'kurulum.iphone1': 'Open Settings and go to Apps → Mail (on iOS 17 and earlier, Mail is in the main list).',
    'kurulum.iphone2': 'Tap Mail Accounts → Add Account → Other → Add Mail Account.',
    'kurulum.iphone3': 'Type your name, your e-mail address and your password, then tap Next.',
    'kurulum.iphone4': 'At the top, make sure IMAP is the selected tab. Fill in the Incoming and Outgoing Mail Server sections from the table below. The username is your full e-mail address in BOTH sections — the outgoing one too, even though iOS calls it optional.',
    'kurulum.iphone5': 'Tap Save. If it takes a moment to verify, that is normal.',

    'kurulum.macBaslik': 'Mac (Apple Mail)',
    'kurulum.mac1': 'Open Mail, then choose Mail → Settings → Accounts.',
    'kurulum.mac2': 'Click + at the bottom left and choose “Other Mail Account”.',
    'kurulum.mac3': 'Type your name, your e-mail address and your password, then click Sign In. When it says the account must be set up manually, that is expected.',
    'kurulum.mac4': 'Choose IMAP, then fill in the incoming and outgoing servers from the table below and click Sign In.',

    'kurulum.elleBaslik': 'Any other mail app — the settings',
    'kurulum.elleMetin': 'Anything that speaks IMAP works. These are the four values every app asks for.',
    'kurulum.gelen': 'Incoming (IMAP)',
    'kurulum.giden': 'Outgoing (SMTP)',
    'kurulum.sifreleme': 'SSL/TLS',
    'kurulum.kullaniciAdi': 'Username',
    'kurulum.kullaniciAdiDeger': 'your full e-mail address, not just the part before the @',
    'kurulum.parola': 'Password',
    'kurulum.parolaDeger': 'the same one you use here',
    'kurulum.elleUyari': 'If an app offers “None” or “STARTTLS” for encryption, choose SSL/TLS. Our server does not accept an unencrypted connection, so anything else will simply fail to connect.',
    'admin.logoTitle': "Your company's logo",
    'admin.logoHint': 'Shown on the sign-in screen of your domain, before anybody signs in. PNG, JPEG or WebP, at most 256 KB and 1024×1024. SVG is not accepted: it is a document and can carry script.',
    'admin.logoChoose': 'Choose a file',
    'admin.logoUploading': 'Uploading…',
    'admin.logoRemove': 'Remove',
    'admin.logoDone': 'The logo is up. It may take a few minutes to appear everywhere.',
    'admin.logoRemoved': 'The logo has been removed.',
    'admin.title': 'Mailboxes in your domains',
    'admin.none': 'No mailboxes yet.',
    'admin.newTitle': 'Open a new mailbox',
    'admin.address': 'Address',
    'admin.name': 'Name',
    'admin.password': 'Password',
    'admin.create': 'Open mailbox',
    'admin.created': 'Mailbox opened.',
    'admin.resetTitle': 'Reset a password',
    'admin.resetFor': 'Which mailbox',
    'admin.reset': 'Reset password',
    'admin.resetDone': 'Password changed.',
    'admin.passwordHint': 'At least 10 characters. Nobody can read it back afterwards — write it down before you save.',
    'admin.inactive': 'closed',
    'admin.hint': 'These changes happen on the mail server straight away, and every one of them is recorded with your address.',
    'admin.sharedTitle': 'Shared address book and calendar',
    'admin.sharedHint': 'One address book and one calendar for the whole domain: everyone with an address here can read and write them, and nobody else can see them. Phones do not find these on their own — the address has to be given once.',
    'admin.sharedAgain': 'Run again',
    'admin.sharedContacts': 'Shared address book',
    'admin.sharedCalendar': 'Shared calendar',
    'admin.authorityHint':
        'You can do this because the mail server lists your mailbox as an administrator of this domain — nothing here decides it. Every mailbox opened and every password reset is recorded against your address.',
    'admin.sharedOpen': 'Open them',
    'admin.sharedDone': 'Ready at {path} — address book {contacts}, calendar {calendar}.',
    'admin.domains': 'you administer',
    'admin.allDomains': 'every domain',
    'forwarding.notRunning': 'These settings are stored but the server is not running them.',
    'forwarding.limitReached': 'This mail server forwards to at most {n} addresses. Remove one to add another.',

    'reader.more': 'More actions',
    'reader.makeRule': 'Make a rule from this message',

    'block.title': 'Blocked senders',
    'block.add': 'Block',
    'block.bad': '“{value}” is neither an address nor a domain. Write someone@example.com, or @example.com for everyone there.',
    'block.already': '{value} is already blocked.',
    'block.sweep': 'Sweep old mail from {value}',
    'block.sweeping': 'Sweeping…',
    'block.swept': 'Moved {count} messages to {folder}.',
    'block.whereTitle': 'where blocked mail goes',
    'block.toJunk': 'Into a folder',
    'block.toNowhere': 'Delete on arrival',
    'block.hint': 'Blocking only affects mail that arrives from now on. Sweep to deal with what is already here.',
    'block.sender': 'Block this sender',
    'block.domain': 'Block everyone at {domain}',
    'block.done': '{value} is blocked. New mail from them goes to {folder}.',

    'vacation.title': 'Automatic reply',
    'vacation.on': 'on',
    'vacation.off': 'off',
    'vacation.onNow': 'auto-reply is on',
    'vacation.offNow': 'auto-reply is off',
    'vacation.from': 'Starts',
    'vacation.to': 'Ends',
    'vacation.subject': 'Subject',
    'vacation.subjectHint': 'Out of office',
    'vacation.body': 'Message',
    'vacation.turnOn': 'Turn on',
    'vacation.saveOn': 'Save',
    'vacation.turnOff': 'Turn off',
    'vacation.hint': 'The server answers each sender once, not once per message, so two auto-repliers cannot get stuck talking to each other. Leave the dates empty to reply until you turn this off.',
    'vacation.unsupported': 'This mail server does not offer an automatic reply.',

    'settings.appearance': 'Appearance',
    'settings.layout': 'Layout',
    'settings.keyboard': 'Keyboard',
    'settings.rules': 'Rules & filters',
    'settings.forwarding': 'Forwarding & auto-reply',
    'settings.adminGroup': 'Administrator',
    'settings.domainAdmin': 'Domain management',
    'settings.vacation': 'Automatic reply',
    'settings.identity': 'Identity & signature',
    'settings.contacts': 'Contacts',
    'settings.accounts': 'Accounts',
    'settings.admin': 'Mailboxes',
    'settings.language': 'language',
    'settings.baseTheme': 'base theme',
    'settings.dark': 'Dark',
    'settings.light': 'Light',
    'settings.system': 'System',
    'settings.density': 'density',
    'settings.comfortable': 'Comfortable',
    'settings.cosy': 'Cosy',
    'settings.compact': 'Compact',
    'settings.baseSize': 'base size',
    'settings.readingPane': 'reading pane',
    'settings.paneRight': 'Right of the list',
    'settings.paneBottom': 'Below the list',
    'settings.paneOff': 'Off — open full width',
    'settings.spaceSplit': 'space split',
    'settings.balanced': 'Balanced',
    'settings.wideReading': 'Narrow list, wide reading',
    'settings.dragHint': 'You can also drag the divider between the panes; that size is remembered.',
    'settings.paneOffHint': 'With the reading pane off, opening a message replaces the list and esc takes you back.',
    'settings.conversations': 'conversations',
    'settings.threadsOn': 'Show the conversation',
    'settings.undoSend': 'Undo send',
    'settings.undoOff': 'Off',
    'settings.undoSeconds': '{count}s',
    'settings.undoHint': 'Send waits this long before the message leaves, so it can still be taken back. Nothing has reached the server during the wait.',
    'settings.threadsOff': 'This message only',

    'settings.tokensLive': 'tokens are live — type and watch',
    'settings.revert': 'Revert',
    'settings.resetToken': 'Reset',
    'settings.savedThemes': 'saved themes',
    'settings.apply': 'Apply',
    'settings.copy': 'Copy',
    'settings.delete': 'Delete',
    'settings.noSavedThemes': 'No saved themes yet.',
    'settings.nameTheme': 'Name this theme',
    'settings.saveCurrent': 'Save current',
    'settings.pasteTheme': 'Paste an exported theme',
    'settings.import': 'Import',
    'settings.tokenCount': '{count} tokens',

    'keys.preset': 'preset',
    'keys.custom': 'Custom',
    'keys.capture': 'click a key, then press the one you want · esc cancels',
    'keys.pressing': 'press…',
    'keys.clash': 'Two actions share a key — the first one listed wins and the other silently does nothing.',
    'keys.resetPreset': 'Reset to the Gmail preset',
    'keys.physicalHint': "Keys are stored by physical position, not by the character they type — so a binding stays one keystroke whether you're on Turkish Q, US or anything else.",
    'keys.scope.global': 'global',
    'keys.scope.list': 'list',
    'keys.title': 'Keyboard shortcuts',
    'keys.guideHint': 'Press ? to open this, esc to close. Change any of them in Settings › Keyboard.',
    'keys.scope.reading': 'reading',
    'keys.action.next': 'Next message',
    'keys.action.prev': 'Previous message',
    'keys.action.compose': 'New message',
    'keys.action.reply': 'Reply',
    'keys.action.replyAll': 'Reply all',
    'keys.action.forward': 'Forward',
    'keys.action.archive': 'Archive',
    'keys.action.move': 'Move to folder',
    'keys.action.delete': 'Delete',
    'keys.action.star': 'Star',
    'keys.action.unread': 'Mark unread',
    'keys.action.refresh': 'Refresh',

    'accounts.count.one': '{count} mailbox',
    'accounts.count.other': '{count} mailboxes',
    'accounts.picture': 'Picture',
    'accounts.changePicture': 'Change picture',
    'accounts.removePicture': 'Remove',
    'accounts.signOut': 'Sign out',
    'accounts.add': 'Add mailbox',
    'accounts.pictureHint': "Pictures are stored on this device only — the mail protocol has nowhere to put one, so they won't follow you to another machine.",
    'accounts.state.ready': 'ready',
    'accounts.state.connecting': 'connecting',
    'accounts.state.error': 'not connected',
    'accounts.state.idle': 'idle',

    'rules.addMailboxFirst': 'Add a mailbox first.',
    'rules.rulesFor': 'rules for',
    'rules.perMailbox': 'rules run on the server, per mailbox',
    'rules.foreign': 'This mailbox has a filter script that was not written here — probably edited by hand. The builder shows what it can read, and saving replaces the whole script. Check the Sieve below first.',
    'rules.inactive': 'None of these rules are running. The server is running a different script — “{name}”. Saving here makes this one active again.',
    'rules.brokenOne': 'One rule points at a folder that no longer exists. Until you pick a new one it will file into a folder recreated from the old name, which looks like it worked. Missing: {list}.',
    'rules.brokenMany': '{count} rules point at a folder that no longer exists. Until you pick new ones they will file into folders recreated from the old names, which looks like it worked. Missing: {list}.',
    'rules.connecting': 'connecting and loading rules…',
    'rules.cannotConnect': 'could not connect to this mailbox',
    'rules.saveDisabled': 'Save stays disabled until this finishes.',
    'rules.on': 'on',
    'rules.delete': 'Delete',
    'rules.runEarlier': 'Run earlier',
    'rules.runLater': 'Run later',
    'rules.if': 'if',
    'rules.matchAll': 'all of these',
    'rules.matchAny': 'any of these',
    'rules.addCondition': '+ condition',
    'rules.then': 'then',
    'rules.addAction': '+ action',
    'rules.stopAfter': 'stop after this rule',
    'rules.headerWarning': 'Careful with forwarded mail. From, To and Cc are headers, and forwarding does not rewrite them — they still show the original sender and recipient. To match where the mail actually arrived, use “Delivered to” or “Delivered to (+ part)”.',
    'rules.manyWildcards': 'more than one wildcard here — ${1} refers to the last one that matched, which is rarely what you want. Prefer one wildcard per rule.',
    'rules.wildcardHint': 'this rule uses a wildcard — put ${1} in a folder name to use whatever the * matched. The folder is created automatically.',
    'rules.add': '+ Add rule',
    'rules.showSieve': 'Show Sieve',
    'rules.hideSieve': 'Hide Sieve',
    'rules.save': 'Save rules',
    'rules.saving': 'Saving…',
    'rules.checking': 'Checking with the server…',
    'rules.saved': 'Saved and active on the server.',
    'rules.rejected': 'The server rejected these rules: {message}',
    'rules.saveTitle': 'Validate and save these rules on the server',
    'rules.saveWaitTitle': 'Still connecting to this mailbox',
    'rules.footer': 'These run on the server as mail arrives, so they work whether or not this app is open. “Delivered to” matches the real envelope address, which is what makes catch-all rules possible. Blocked senders and forwarding run before these.',
    'rules.folderGone': '{name} — folder is gone',
    'rules.patternOption': 'Pattern or new folder…',
    'rules.field.from': 'From',
    'rules.field.to': 'To',
    'rules.field.cc': 'Cc',
    'rules.field.subject': 'Subject',
    'rules.field.recipient': 'Delivered to',
    'rules.field.detail': 'Delivered to (+ part)',
    'rules.field.body': 'Body text',
    'rules.hint.header': 'the header — a forward does NOT rewrite this',
    'rules.hint.recipient': 'the real envelope address, even for a catch-all or a forward',
    'rules.hint.detail': 'just the bit after the + in you+something@domain',
    'rules.op.contains': 'contains',
    'rules.op.is': 'is exactly',
    'rules.op.matches': 'matches pattern',
    'rules.op.regex': 'matches regex',
    'rules.hint.matches': 'use * as a wildcard — what it matches becomes ${1}',
    'rules.act.fileinto': 'Move to folder',
    'rules.act.seen': 'Mark as read',
    'rules.act.flagged': 'Star it',
    'rules.act.forward': 'Forward to',
    'rules.act.discard': 'Delete it',

    'tokens.surfaces': 'surfaces',
    'tokens.text': 'text',
    'tokens.accent': 'accent',
    'tokens.semantic': 'semantic',

    'theme.toggleToLight': 'Switch to the light theme',
    'theme.toggleToDark': 'Switch to the dark theme',
} as const;

export type Key = keyof typeof en;

export const tr: Record<Key, string> = {
    'app.settings': 'Ayarlar',
    'nav.mail': 'Posta',
    'nav.folders': 'Klasörler',
    'app.backToMail': 'postaya dön',
    'app.loading': 'yükleniyor',
    'app.cancel': 'Vazgeç',
    'app.save': 'Kaydet',
    'app.delete': 'Sil',
    'app.create': 'Oluştur',
    'app.rename': 'Yeniden adlandır',
    'app.close': 'Kapat',
    'app.retry': 'Tekrar dene',
    'app.on': 'açık',
    'app.off': 'Kapalı',

    'login.addMailbox': 'posta kutusu ekle',
    'login.intro':
        'Her posta kutusu ayrı ayrı giriş yapar — sunucuda bağımsız hesaplardır.',
    'login.address': 'E-posta adresi',
    'login.password': 'Parola',
    'login.signInTo': 'Posta kutuna giriş yap',
    'login.passwordNote':
        'Parolan yalnızca kendi posta altyapına iletilir — posta, takvim/rehber ve yönetim servislerine. Başka hiçbir yere gitmez, hiçbir yere yazılmaz, çıkış yaptığında silinir.',
    'login.remember': 'Beni hatırla',
    'login.rememberNote': 'Bu cihazda bir hafta açık kalırsın. Ortak bir bilgisayardaysan işaretleme.',
    'login.submit': 'Giriş yap',
    'login.working': 'Giriş yapılıyor…',
    'login.rejected': 'Bu adres ve parola kabul edilmedi.',
    'login.sessionEnded': 'Oturumun sona erdi. Devam etmek için yeniden giriş yap.',
    'session.ended': '{address} oturumu sona erdi.',
    'login.tooMany': 'Çok fazla giriş denemesi yapıldı. Birkaç dakika bekleyip tekrar dene.',
    'login.failed': 'Posta sunucusuna ulaşılamadı. Birazdan tekrar dene.',
    'login.needBoth': 'Hem adresini hem parolanı gir.',
    'login.added': 'eklendi',

    'folders.role.inbox': 'Gelen Kutusu',
    'folders.role.drafts': 'Taslaklar',
    'folders.role.sent': 'Gönderilenler',
    'folders.role.archive': 'Arşiv',
    'folders.role.junk': 'Gereksiz',
    'folders.role.trash': 'Çöp Kutusu',
    'account.switchOrAdd': 'değiştir veya ekle',
    'account.yours': 'posta kutunuz',
    'brand.operatorFor': '{name} için',
    'account.count': '{count} posta kutusu',
    'folders.title': 'klasörler',
    'folders.new': 'Yeni klasör',
    'folders.actions': 'Klasör işlemleri',
    'folders.newSub': 'Yeni alt klasör…',
    'folders.rename': 'Yeniden adlandır…',
    'folders.deleteDots': 'Sil…',
    'folders.hide': 'Bu listeden gizle',
    'folders.show': 'Bu listede göster',
    'folders.toTop': 'En üst seviyeye taşı',
    'folders.name': 'Klasör adı',
    'folders.newIn': '{name} içinde yeni klasör',
    'folders.renaming': '{name} adını değiştir',
    'folders.deleting': '{name} klasörünü sil',
    'folders.deleteEmpty': 'Klasör boş. Bu işlem geri alınamaz.',
    'folders.deleteFull':
        'Bu klasörde {count} ileti var ve klasörü silmek onları da siler. Geri alınamaz. Onaylamak için {name} yaz.',
    'folders.empty': 'Boşalt…',
    'folders.emptying': '{name} boşaltılsın mı?',
    'folders.emptyBody': '{name} içindeki {count} ileti kalıcı olarak silinecek. Geri alınamaz.',
    'folders.emptyConfirm': 'Boşalt',
    'folders.emptied': '{name} boşaltıldı — {count} ileti kalıcı olarak silindi',
    'folders.hiddenCount': '{count} gizli',
    'folders.hiddenOne': '1 gizli klasör',
    'folders.hiddenMany': '{count} gizli klasör',
    'folders.showHidden': 'gizli klasörleri göster',
    'folders.hideHidden': 'tekrar gizle',
    'folders.noRole':
        '{names} için sunucuda bir rol tanımlı değil, bu yüzden diğer posta uygulamaları ne olduğunu bilmiyor.',
    'folders.noRoleMany':
        '{names} için sunucuda rol tanımlı değil, bu yüzden diğer posta uygulamaları ne olduklarını bilmiyor.',
    'folders.fixRoles': 'Tüm uygulamalar için düzelt',
    'folders.fixing': 'Düzeltiliyor…',
    'folders.moveTo': 'Şuraya taşı…',
    'folders.createNamed': '“{name}” oluştur',
    'folders.noMatch': 'Eşleşen klasör yok.',

    'list.messages': '{count} ileti',
    'list.conversations': '{count} konuşma',
    'list.unread': '{count} okunmamış',
    'list.empty': 'Bu klasörde bir şey yok.',
    'list.emptyFolder': 'Bu klasörü boşalt',
    'list.noSubject': '(konu yok)',
    'list.searchPlaceholder': 'Ara — her yerde aramak için başına all: yaz',
    'list.searchTitle': 'Arama',
    'list.searching': 'aranıyor…',
    'list.results': '{count} sonuç',
    'list.resultsOne': '1 sonuç',
    'list.inAllMail': 'tüm postalar',
    'list.inThisFolder': 'bu klasör',
    'list.clear': 'Temizle (esc)',
    'list.draftTo': 'Alıcı: {name}',
    'keys.action.send': 'Gönder',
    'keys.action.selectAll': 'Yüklenenlerin tümünü seç',
    'keys.action.search': 'Ara',
    'keys.scope.compose': 'yazarken',
    'list.aria': 'İleti listesi',
    'list.rowSelect': 'Bu iletiyi seç',
    'list.stUnread': 'Okunmadı',
    'list.stFlagged': 'Yıldızlı',
    'list.stAttachment': 'Ekli dosya var',
    'list.stDraft': 'Taslak',
    'list.stThread': '{count} iletilik konuşma',

    'reader.nothing': 'seçili ileti yok',
    'reader.pick': 'Bir ileti seç.',
    'reader.noBody': '(okunabilir içerik yok)',
    'reader.threadFailed': 'Bu ileti yüklenemedi.',
    'reader.openMessage': 'İletiyi aç',
    'reader.to': 'alıcı: {names}',
    'reader.from': 'gönderen: {names}',
    'reader.back': '{name} listesine dön',
    'reader.blocked': 'Gizliliğin için {count} uzak görsel engellendi.',
    'reader.blockedOne': 'Gizliliğin için 1 uzak görsel engellendi.',
    'reader.blockedWhy': 'gönderenler bunlarla iletiyi ne zaman açtığını anlayabilir',
    'reader.loadImages': 'Görselleri yükle',
    'reader.attachments': '{count} ek',
    'reader.attachmentsOne': '1 ek',
    'reader.unnamed': '(adsız)',
    'reader.thisMessage': 'bu ileti',
    'reader.spoofTitle': 'Bu ileti göründüğü adresten gelmemiş olabilir.',
    'reader.spoofBody': 'Bir bağlantıya tıklamadan ya da ödeme yapmadan önce gönderenle başka bir yoldan doğrulayın.',
    'reader.historyOne': 'Bu konuşmada 1 önceki ileti var',
    'reader.history': 'Bu konuşmada {count} önceki ileti var',

    'action.reply': 'Yanıtla',
    'action.replyAll': 'Tümünü yanıtla',
    'action.forward': 'Yönlendir',
    'action.archive': 'Arşivle',
    'action.move': 'Klasöre taşı',
    'action.trash': 'Sil',
    'action.star': 'Yıldızla',
    'action.markRead': 'Okundu işaretle',
    'action.markUnread': 'Okunmadı işaretle',
    'action.compose': 'Yeni ileti',
    'action.newMessage': 'Yeni ileti',
    'action.refresh': 'Yenile',
    'action.undo': 'Geri al',
    'toast.movedTo': '{folder} klasörüne taşındı',
    'toast.trashed': 'Çöp kutusuna taşındı',
    'toast.erased': 'Kalıcı olarak silindi',
    'toast.archived': 'Arşivlendi',
    'toast.movedCount': '{count} ileti {folder} klasörüne taşındı',
    'compose.sessionLost': 'Oturumun sona erdi, bu yüzden şu an sunucuya kaydedilemiyor. Yazdıkların duruyor — başka bir sekmede yeniden giriş yap, sonra bu pencereyi kapat ki kaydedilsin.',
    'action.next': 'Sonraki ileti',
    'action.prev': 'Önceki ileti',

    'compose.to': 'kime',
    'compose.cc': 'bilgi',
    'compose.bcc': 'gizli',
    'compose.ccBcc': 'bilgi/gizli',
    'compose.subject': 'konu',
    'compose.send': 'Gönder',
    'compose.sending': 'Gönderiliyor…',
    'compose.attach': 'Dosya ekle',
    'compose.uploading': 'yükleniyor…',
    'compose.discard': 'Vazgeç',
    'compose.saveQuestion': 'Bu ileti taslak olarak kaydedilsin mi?',
    'compose.saveFailed': 'Taslak kaydedilemedi. Yazdıkların duruyor — biraz sonra yeniden dene.',
    'compose.saveDraft': 'Taslağa kaydet',
    'compose.dontSave': 'Kaydetme',
    'compose.keepWriting': 'Yazmaya devam et',
    'compose.unsaved': 'kaydedilmedi',
    'compose.dropHere': 'Eklemek için bırak',
    'compose.undo': 'Geri al ({seconds})',
    'compose.sendNow': 'Hemen gönder',
    'compose.later': 'Sonra',
    'compose.schedule': 'Zamanlanmış gönderim',
    'compose.scheduleSend': 'Bu saatte gönder',
    'compose.schedulePast': 'Bu zaman çoktan geçti.',
    'compose.scheduleTooFar': 'Sunucu bir iletiyi en fazla 30 gün bekletir.',
    'compose.tomorrowMorning': 'Yarın sabah',
    'compose.thisEvening': 'Bu akşam',
    'compose.mondayMorning': 'Pazartesi sabahı',
    'compose.failed': 'başarısız',
    'compose.badAddress': 'Geçerli bir adres değil: {list}',
    'compose.plainText': 'düz metin',
    'compose.contactChip': 'rehberde',

    'rich.bold': 'Kalın',
    'rich.italic': 'İtalik',
    'rich.underline': 'Altı çizili',
    'rich.strike': 'Üstü çizili',
    'rich.bullets': 'Madde işaretli liste',
    'rich.numbers': 'Numaralı liste',
    'rich.quote': 'Alıntı',
    'rich.link': 'Bağlantı',
    'rich.image': 'Görsel ekle',
    'rich.clear': 'Biçimi temizle',
    'rich.apply': 'Uygula',
    'rich.placeholder.signature': 'İmzanı buraya yaz…',
    'rich.placeholder.vacation': 'Karşı tarafa gidecek yanıtı yaz…',
    'rich.placeholder.compose': 'İletini yaz…',

    'identity.save': 'Kaydet',
    'identity.saving': 'Kaydediliyor…',
    'identity.saved': 'kaydedildi',
    'identity.displayName': 'Görünen ad',
    'identity.signature': 'İmza',
    'identity.plainSignature': 'Düz metin imzası',
    'identity.editPlain': 'Düz metin sürümü',
    'identity.hidePlain': 'Düz metni gizle',
    'identity.logoHint': 'Buraya eklediğiniz görsel iletinin bir parçası olarak gider; uzak görselleri engelleyen uygulamalarda bile görünür.',
    'identity.plainHint': 'HTML olmadan okuyanlara gider. Siz burada değiştirene kadar üstteki imzayı izler.',
    'identity.mailbox': 'Posta kutusu',
    'identity.localHint':
        'Adın ve imzan posta kutunda, summail adlı bir klasörde saklanır — yani girdiğin her bilgisayarda seninle birlikte gelir.',
    'identity.noAccount': 'Önce bir posta kutusuna giriş yapın.',
    'compose.richText': 'biçimli',
    'compose.fullScreen': 'Tam ekran',
    'compose.dock': 'Küçült',
    'compose.closeKeepsDraft': 'Kapat — taslak olarak saklanır (esc)',
    'compose.loadingIdentity': 'kimlik yükleniyor',
    'compose.from': 'kimden',


    'search.ignored': 'Bu sunucu şunlarla arama yapamıyor: {list}. Aramanın kalanı uygulandı.',
    'sort.title': 'Sırala',
    'sort.receivedAt': 'Gelme',
    'sort.sentAt': 'Gönderilme',
    'sort.from': 'Gönderen',
    'sort.to': 'Alıcı',
    'sort.subject': 'Konu',
    'sort.size': 'Boyut',

    'bulk.selected': '{count} seçili',
    'bulk.selectLoaded': 'Yüklenen {count} iletiyi seç',
    'bulk.clear': 'Temizle',

    'storage.of': '{limit} alanın {used} kadarı',

    'reader.showQuoted': 'Alıntılanan kısmı göster',
    'reader.hideQuoted': 'Alıntılanan kısmı gizle',
    'reader.print': 'Yazdır',
    'reader.download': '.eml indir',
    'reader.unsubscribe': 'Abonelikten çık',
    'reader.unsubscribeSent': 'Abonelikten çıkma isteği {to} adresine gönderildi.',
    'preview.save': 'Kaydet',
    'attachment.download': 'İndir',
    'preview.previous': 'Önceki ek',
    'preview.next': 'Sonraki ek',
    'preview.open': 'Ön izleme',
    'preview.converting': 'belge hazırlanıyor…',
    'preview.converted': 'dönüştürüldü',
    'preview.convertedWhy': 'Burada gösterilebilmesi için PDF’e dönüştürüldü. Kaydet, özgün dosyayı indirir.',
    'reader.spam': 'Gereksiz olarak işaretle',
    'reader.notSpam': 'Gereksiz değil',

    'directory.title': 'Sunucu rehberi',
    'directory.link': 'Sunucu',
    'directory.search': 'Kişi ara…',
    'directory.mailboxes': 'Bu sunucuda {count} posta kutusu',
    'directory.none': 'Eşleşen kimse yok.',
    'directory.copy': 'Adresi kopyala',
    'directory.copied': 'kopyalandı',
    'directory.managingTitle': 'Posta kutusu açma ve silme',
    'directory.managingBody': 'Bu posta sunucusu JMAP üzerinden hesap oluşturma, adlandırma veya silme imkânı vermiyor; bu işler buradan değil sunucunun kendi yönetim arayüzünden yapılıyor.',
    'directory.permissionMismatch': 'Sunucu sana hesap yönetimi izinleri listeliyor ama bu izinleri kullanacak metodu sunmuyor.',
    'directory.openAdmin': 'Sunucu yönetim panelini aç',
    'directory.setAdminUrl': 'Doğrudan bağlantı için config.json içindeki “adminUrl” alanını doldur.',
    'directory.privacy': 'Oturum açmış her kullanıcı bu listeyi okuyabilir — sunucudaki her posta kutusunun adı ve adresi. Bu, buradaki bir ayar değil posta sunucusunun davranışı; yani paylaşımlı bir sunucu kiracılar arasında gizli değildir.',
    'contacts.title': 'Rehber',
    'contacts.search': 'Rehberde ara',
    'contacts.new': 'Yeni kişi',
    'contacts.count': '{count} kişi',
    'contacts.import': 'vCard içe aktar',
    'contacts.export': 'Tümünü dışa aktar',
    'contacts.imported': '{count} kişi eklendi.',
    'contacts.saved': 'Kaydedildi.',
    'contacts.delete': 'Kişiyi sil',
    'contacts.pick': 'Soldan birini seçin ya da yeni bir kişi ekleyin.',
    'contacts.empty': 'Henüz kişi yok. Buradan ekleyin ya da bu rehberle eşitlenen telefondan.',
    'contacts.name': 'Ad',
    'contacts.addEmail': 'Adres ekle',
    'contacts.addPhone': 'Telefon ekle',
    'contacts.removeLine': 'Bu satırı kaldır',
    'contacts.write': 'Yaz',
    'contacts.writeTo': '{address} adresine yaz',
    'contacts.book': 'Rehber defteri',
    'contacts.myBook': 'Kişilerim',
    'contacts.sharedBook': '{domain} Rehberi · ortak',
    'contacts.syncNote': 'Bunlar telefonundaki rehberle eşitlenir.',
    'contacts.shared': 'ortak',
    'contacts.sharedNotice':
        'Bu kayıt {domain} ortak rehberinde. Burada değiştirilen şey, o alan adındaki herkes için değişir.',
    'contacts.unsavedQuestion': 'Bu kişideki değişiklikler kaydedilsin mi?',
    'contacts.keepEditing': 'Düzenlemeye devam et',
    'contacts.deleted': '{name} silindi',
    'contacts.none': 'Bu kurulumda rehber yok.',
    'contacts.needSomething': 'Bir kişide en az ad ya da adres olmalı.',
    'contacts.noMatches': 'Buna uyan kimse yok.',

    'calendar.title': 'Takvim',
    'calendar.today': 'Bugün',
    'calendar.previous': 'Önceki ay',
    'calendar.next': 'Sonraki ay',
    'calendar.newEvent': 'Yeni olay',
    'calendar.noEvents': 'Bu günde bir şey yok.',
    'calendar.name': 'Ne',
    'calendar.allDay': 'Tüm gün',
    'calendar.starts': 'başlangıç',
    'calendar.ends': 'bitiş',
    'calendar.where': 'Nerede',
    'calendar.note': 'Notlar',
    'calendar.which': 'Takvim',
    'calendar.shared': 'ortak',
    'calendar.delete': 'Olayı sil',
    'calendar.saved': 'kaydedildi',
    'calendar.deleted': '{name} silindi',
    'calendar.none': 'Bu kurulumda takvim yok.',
    'calendar.keepEditing': 'Düzenlemeye devam et',
    'calendar.unsavedQuestion': 'Bu olaydaki değişiklikler kaydedilsin mi?',
    'calendar.mine': 'Takvimim',
    'calendar.theirs': '{domain} Takvimi',
    'calendar.sharedBadge': 'Ortak',
    'calendar.sharedNotice':
        'Bu olay {domain} ortak takviminde. Burada değiştirilen şey, o alan adındaki herkes için değişir.',
    'calendar.recurringNotice':
        'Bu olay tekrar ediyor. Başlığı, yeri ve notu buradan değiştirilebilir ve değişiklik bütün tekrarlara işler; saatleri tekrarın kendisine aittir ve oluşturulduğu yerden değiştirilir.',

    'contacts.given': 'Ad',
    'contacts.surname': 'Soyad',
    'contacts.emails': 'e-posta',
    'contacts.phones': 'telefon',
    'contacts.addresses': 'adres',
    'contacts.addressHint': 'Sokak, şehir, ülke',
    'contacts.organization': 'Şirket',
    'contacts.jobTitle': 'Ünvan',
    'contacts.note': 'Notlar',
    'contacts.photo': 'Fotoğrafı değiştir',
    'contacts.add': 'Rehbere ekle',
    'contacts.added': '{name} rehberinize eklendi.',

    'forwarding.title': 'Bir kopyasını şuraya yönlendir',
    'forwarding.on': 'yönlendirme açık',
    'forwarding.add': 'Ekle',
    'forwarding.saved': 'Sunucuya kaydedildi.',
    'forwarding.originalTitle': 'burada kalan kopya',
    'forwarding.keep': 'Gelen Kutusu’nda kalsın',
    'forwarding.archive': 'Bir klasöre taşı',
    'forwarding.delete': 'Sil',
    'forwarding.into': 'klasör',
    'forwarding.hint': 'Yönlendirme, ileti geldiği anda sunucuda ve kurallarınızdan önce çalışır. From ve To başlıklarını değiştirmez — ileti hâlâ ilk gönderen ve ilk alıcıyı gösterir.',
    'forwarding.off': 'yönlendirme kapalı',
    'forwarding.turnOff': 'Kapat',
    'forwarding.remove': 'Çıkar',
    'kurulum.ac': 'Cihazlarınıza nasıl kurulur?',
    'kurulum.kapat': 'Kurulum rehberini gizle',

    'kurulum.otoBaslik': 'En kolayı — kendi kendine kuruluyor',
    'kurulum.otoMetin': 'Thunderbird ve kendi uygulamamız summail, sunucu ayarlarını sizin için buluyor. Sayfanın altındaki tablodan hiçbir şeye ihtiyacınız olmayacak.',
    'kurulum.oto1': 'Uygulamayı açın ve hesap eklemeyi seçin.',
    'kurulum.oto2': 'E-posta adresinizi ve parolanızı yazın. Başka hiçbir şey girmeyin.',
    'kurulum.oto3': 'Uygulama gerisini kendisi bulup kurulumu bitirir. Size seçtirirse IMAP\'i seçin.',

    'kurulum.androidBaslik': 'Android — kendi uygulamamız',
    'kurulum.androidMetin': 'summail postanızı, rehberinizi ve takviminizi telefonda bir arada tutar ve yalnız adresinizden kendini kurar.',
    'kurulum.android1': 'Aşağıdaki düğmeye dokunun, summail\'i Google Play\'de açın ve kurun.',
    'kurulum.android2': 'Uygulamayı açın, e-posta adresinizi ve parolanızı yazın.',
    'kurulum.android3': 'Hepsi bu. Postanız gelir, rehberiniz ve takviminiz eşitlenmeye başlar.',
    'kurulum.androidDugme': 'summail\'i Google Play\'de aç',

    'kurulum.outlookBaslik': 'Windows\'ta Outlook (Microsoft 365 / 2021)',
    'kurulum.outlook1': 'Outlook\'u açın ve sol üstteki Dosya sekmesine tıklayın.',
    'kurulum.outlook2': '“Hesap Ekle”ye tıklayın.',
    'kurulum.outlook3': 'Tam e-posta adresinizi yazın, sonra “Gelişmiş seçenekler”e tıklayıp “Hesabımı el ile ayarlamama izin ver” kutusunu işaretleyin. Bağlan\'a tıklayın.',
    'kurulum.outlook4': 'Çıkan hesap türü listesinden IMAP\'i seçin.',
    'kurulum.outlook5': 'Gelen ve giden sunucuyu sayfanın altındaki tablodan doldurun. Şifreleme yöntemini İKİ bölümde de SSL/TLS yapın — “Yok” asla.',
    'kurulum.outlook6': 'Parolanızı yazıp Bağlan\'a tıklayın. Outlook bağlantıyı sınar ve bittiğini söyler.',
    'kurulum.outlookNot': 'Outlook bizim otomatik ayarlarımızı okumaz, elle doldurulmasının sebebi bu. Bazı sürümlerde hesap türü “Gelişmiş seçenekler” altında değil doğrudan sorulur — orada IMAP\'i seçip aynı şekilde devam edin.',

    'kurulum.iphoneBaslik': 'iPhone ve iPad',
    'kurulum.iphone1': 'Ayarlar\'ı açın, Uygulamalar → Mail\'e gidin (iOS 17 ve öncesinde Mail ana listededir).',
    'kurulum.iphone2': 'Mail Hesapları → Hesap Ekle → Diğer → Mail Hesabı Ekle\'ye dokunun.',
    'kurulum.iphone3': 'Adınızı, e-posta adresinizi ve parolanızı yazıp İleri\'ye dokunun.',
    'kurulum.iphone4': 'Üstte IMAP sekmesinin seçili olduğundan emin olun. Gelen ve Giden Posta Sunucusu bölümlerini aşağıdaki tablodan doldurun. Kullanıcı adı İKİ bölümde de tam e-posta adresinizdir — iOS gideni isteğe bağlı gösterse de yazın.',
    'kurulum.iphone5': 'Kaydet\'e dokunun. Doğrulama biraz sürerse normaldir.',

    'kurulum.macBaslik': 'Mac (Apple Mail)',
    'kurulum.mac1': 'Mail\'i açın, Mail → Ayarlar → Hesaplar\'a gidin.',
    'kurulum.mac2': 'Sol alttaki + düğmesine tıklayıp “Diğer Mail Hesabı”nı seçin.',
    'kurulum.mac3': 'Adınızı, e-posta adresinizi ve parolanızı yazıp Giriş Yap\'a tıklayın. “Hesabın el ile ayarlanması gerekiyor” derse beklenen durum budur.',
    'kurulum.mac4': 'IMAP\'i seçin, gelen ve giden sunucuyu aşağıdaki tablodan doldurun ve Giriş Yap\'a tıklayın.',

    'kurulum.elleBaslik': 'Başka bir posta uygulaması — ayarlar',
    'kurulum.elleMetin': 'IMAP konuşan her uygulama çalışır. Her uygulamanın sorduğu dört değer bunlar.',
    'kurulum.gelen': 'Gelen (IMAP)',
    'kurulum.giden': 'Giden (SMTP)',
    'kurulum.sifreleme': 'SSL/TLS',
    'kurulum.kullaniciAdi': 'Kullanıcı adı',
    'kurulum.kullaniciAdiDeger': 'tam e-posta adresiniz — yalnız @ öncesi değil',
    'kurulum.parola': 'Parola',
    'kurulum.parolaDeger': 'burada kullandığınızın aynısı',
    'kurulum.elleUyari': 'Bir uygulama şifreleme için “Yok” ya da “STARTTLS” önerirse SSL/TLS seçin. Sunucumuz şifresiz bağlantı kabul etmiyor; diğerleri bağlanamaz.',
    'admin.logoTitle': 'Firmanızın logosu',
    'admin.logoHint': 'Alan adınızın giriş ekranında, henüz kimse giriş yapmadan görünür. PNG, JPEG ya da WebP; en çok 256 KB ve 1024×1024. SVG kabul edilmez: SVG bir belgedir ve betik taşıyabilir.',
    'admin.logoChoose': 'Dosya seç',
    'admin.logoUploading': 'Yükleniyor…',
    'admin.logoRemove': 'Kaldır',
    'admin.logoDone': 'Logo kondu. Her yerde görünmesi birkaç dakika alabilir.',
    'admin.logoRemoved': 'Logo kaldırıldı.',
    'admin.title': 'Alan adlarınızdaki kutular',
    'admin.none': 'Henüz kutu yok.',
    'admin.newTitle': 'Yeni kutu aç',
    'admin.address': 'Adres',
    'admin.name': 'Ad',
    'admin.password': 'Parola',
    'admin.create': 'Kutuyu aç',
    'admin.created': 'Kutu açıldı.',
    'admin.resetTitle': 'Parola sıfırla',
    'admin.resetFor': 'Hangi kutu',
    'admin.reset': 'Parolayı sıfırla',
    'admin.resetDone': 'Parola değişti.',
    'admin.passwordHint': 'En az 10 karakter. Kaydettikten sonra kimse geri okuyamaz — kaydetmeden önce bir yere yazın.',
    'admin.inactive': 'kapalı',
    'admin.hint': 'Bu değişiklikler posta sunucusunda hemen olur ve her biri sizin adresinizle kayda geçer.',
    'admin.sharedTitle': 'Ortak rehber ve takvim',
    'admin.sharedHint': 'Alan adının tamamı için tek bir rehber ve tek bir takvim: buradaki adresi olan herkes okuyup yazabilir, başkası göremez. Telefonlar bunları kendiliğinden bulmaz — adresin bir kez verilmesi gerekir.',
    'admin.sharedAgain': 'Yeniden çalıştır',
    'admin.sharedContacts': 'Ortak rehber',
    'admin.sharedCalendar': 'Ortak takvim',
    'admin.authorityHint':
        'Bunu yapabiliyorsun çünkü posta sunucusu bu alanın yöneticileri arasında senin kutunu sayıyor — burada karar veren bir şey yok. Açılan her kutu ve sıfırlanan her parola senin adresine kaydediliyor.',
    'admin.sharedOpen': 'Aç',
    'admin.sharedDone': '{path} hazır — rehber {contacts}, takvim {calendar}.',
    'admin.domains': 'yönettiğiniz',
    'admin.allDomains': 'bütün alan adları',
    'forwarding.limitReached': 'Bu posta sunucusu en çok {n} adrese yönlendirir. Başka bir adres eklemek için birini çıkarın.',
    'forwarding.foreign': 'Bu posta kutusunda buradan yazılmamış bir filtre betiği var — büyük ihtimalle elle kurulmuş. Aşağıda gösteriliyor ve dokunulmuyor: buradan değiştirmek, birinin güvendiği postayı durdurabilir.',
    'forwarding.foreignScript': 'sunucudaki betik',
    'forwarding.notRunning': 'Bu ayarlar kayıtlı ama sunucu onları çalıştırmıyor.',

    'reader.more': 'Diğer işlemler',
    'reader.makeRule': 'Bu iletiden kural oluştur',

    'block.title': 'Engellenen göndericiler',
    'block.add': 'Engelle',
    'block.bad': '“{value}” ne bir adres ne de bir alan adı. someone@example.com yazın ya da oradaki herkes için @example.com.',
    'block.already': '{value} zaten engelli.',
    'block.sweep': '{value} adresinden gelen eski iletileri süpür',
    'block.sweeping': 'Süpürülüyor…',
    'block.swept': '{count} ileti {folder} klasörüne taşındı.',
    'block.whereTitle': 'engellenen iletiler nereye gitsin',
    'block.toJunk': 'Bir klasöre',
    'block.toNowhere': 'Gelir gelmez sil',
    'block.hint': 'Engelleme yalnızca bundan sonra gelen iletiler için geçerlidir. Hâlihazırda duranlar için süpürmeyi kullanın.',
    'block.sender': 'Bu göndericiyi engelle',
    'block.domain': '{domain} alan adındaki herkesi engelle',
    'block.done': '{value} engellendi. Yeni iletileri {folder} klasörüne gidecek.',

    'vacation.title': 'Otomatik yanıt',
    'vacation.on': 'açık',
    'vacation.off': 'kapalı',
    'vacation.onNow': 'otomatik yanıt açık',
    'vacation.offNow': 'otomatik yanıt kapalı',
    'vacation.from': 'Başlangıç',
    'vacation.to': 'Bitiş',
    'vacation.subject': 'Konu',
    'vacation.subjectHint': 'Ofis dışındayım',
    'vacation.body': 'İleti',
    'vacation.turnOn': 'Aç',
    'vacation.saveOn': 'Kaydet',
    'vacation.turnOff': 'Kapat',
    'vacation.hint': 'Sunucu her göndericiye ileti başına değil, bir kez yanıt verir; böylece iki otomatik yanıt birbirine takılıp kalmaz. Siz kapatana kadar yanıt vermesi için tarihleri boş bırakın.',
    'vacation.unsupported': 'Bu posta sunucusu otomatik yanıt sunmuyor.',

    'settings.appearance': 'Görünüm',
    'settings.layout': 'Yerleşim',
    'settings.keyboard': 'Klavye',
    'settings.rules': 'Kurallar ve filtreler',
    'settings.forwarding': 'Yönlendirme ve otomatik yanıt',
    'settings.adminGroup': 'Yönetici',
    'settings.domainAdmin': 'Alan yönetimi',
    'settings.vacation': 'Otomatik yanıt',
    'settings.identity': 'Kimlik ve imza',
    'settings.contacts': 'Rehber',
    'settings.accounts': 'Hesaplar',
    'settings.admin': 'Posta kutuları',
    'settings.language': 'dil',
    'settings.baseTheme': 'temel tema',
    'settings.dark': 'Koyu',
    'settings.light': 'Açık',
    'settings.system': 'Sistem',
    'settings.density': 'yoğunluk',
    'settings.comfortable': 'Ferah',
    'settings.cosy': 'Orta',
    'settings.compact': 'Sıkışık',
    'settings.baseSize': 'yazı boyutu',
    'settings.readingPane': 'okuma bölmesi',
    'settings.paneRight': 'Listenin sağında',
    'settings.paneBottom': 'Listenin altında',
    'settings.paneOff': 'Kapalı — tam genişlikte aç',
    'settings.spaceSplit': 'alan paylaşımı',
    'settings.balanced': 'Dengeli',
    'settings.wideReading': 'Dar liste, geniş okuma',
    'settings.dragHint': 'Bölmeler arasındaki çizgiyi de sürükleyebilirsin; bu boyut hatırlanır.',
    'settings.paneOffHint':
        'Okuma bölmesi kapalıyken bir iletiyi açmak listenin yerini alır, esc ile geri dönersin.',
    'settings.conversations': 'konuşmalar',
    'settings.threadsOn': 'Konuşmayı göster',
    'settings.undoSend': 'Göndermeyi geri al',
    'settings.undoOff': 'Kapalı',
    'settings.undoSeconds': '{count} sn',
    'settings.undoHint': 'Gönder, ileti çıkmadan önce bu kadar bekler; bu sürede geri alabilirsin. Bekleme boyunca sunucuya hiçbir şey gitmez.',
    'settings.threadsOff': 'Yalnız bu ileti',

    'settings.tokensLive': 'değerler canlı — yaz ve izle',
    'settings.revert': 'Geri al',
    'settings.resetToken': 'Sıfırla',
    'settings.savedThemes': 'kayıtlı temalar',
    'settings.apply': 'Uygula',
    'settings.copy': 'Kopyala',
    'settings.delete': 'Sil',
    'settings.noSavedThemes': 'Henüz kayıtlı tema yok.',
    'settings.nameTheme': 'Bu temaya bir ad ver',
    'settings.saveCurrent': 'Şimdikini kaydet',
    'settings.pasteTheme': 'Dışa aktarılmış bir temayı yapıştır',
    'settings.import': 'İçe aktar',
    'settings.tokenCount': '{count} değer',

    'keys.preset': 'hazır düzen',
    'keys.custom': 'Kendi düzenim',
    'keys.capture': 'bir tuşa tıkla, sonra istediğin tuşa bas · esc vazgeçer',
    'keys.pressing': 'bas…',
    'keys.clash': 'İki işlem aynı tuşu paylaşıyor — listedeki ilki çalışır, diğeri sessizce hiçbir şey yapmaz.',
    'keys.resetPreset': 'Gmail düzenine döndür',
    'keys.physicalHint': 'Tuşlar yazdıkları karaktere göre değil, klavyedeki fiziksel konumlarına göre saklanır — yani Türkçe Q, US ya da başka bir düzendeyken de aynı tek tuş çalışır.',
    'keys.scope.global': 'her yerde',
    'keys.scope.list': 'listede',
    'keys.title': 'Klavye kısayolları',
    'keys.guideHint': 'Açmak için ?, kapatmak için esc. Hepsini Ayarlar › Klavye’den değiştirebilirsin.',
    'keys.scope.reading': 'okurken',
    'keys.action.next': 'Sonraki ileti',
    'keys.action.prev': 'Önceki ileti',
    'keys.action.compose': 'Yeni ileti',
    'keys.action.reply': 'Yanıtla',
    'keys.action.replyAll': 'Tümünü yanıtla',
    'keys.action.forward': 'İlet',
    'keys.action.archive': 'Arşivle',
    'keys.action.move': 'Klasöre taşı',
    'keys.action.delete': 'Sil',
    'keys.action.star': 'Yıldızla',
    'keys.action.unread': 'Okunmadı işaretle',
    'keys.action.refresh': 'Yenile',

    'accounts.count.one': '{count} posta kutusu',
    'accounts.count.other': '{count} posta kutusu',
    'accounts.picture': 'Fotoğraf',
    'accounts.changePicture': 'Fotoğrafı değiştir',
    'accounts.removePicture': 'Kaldır',
    'accounts.signOut': 'Çıkış yap',
    'accounts.add': 'Posta kutusu ekle',
    'accounts.pictureHint': 'Fotoğraflar yalnızca bu cihazda saklanır — posta protokolünde onları koyacak bir yer yok, bu yüzden başka bir makineye taşınmazlar.',
    'accounts.state.ready': 'hazır',
    'accounts.state.connecting': 'bağlanıyor',
    'accounts.state.error': 'bağlanamadı',
    'accounts.state.idle': 'beklemede',

    'rules.addMailboxFirst': 'Önce bir posta kutusu ekle.',
    'rules.rulesFor': 'kurallar şunun için',
    'rules.perMailbox': 'kurallar sunucuda, her posta kutusu için ayrı çalışır',
    'rules.foreign': 'Bu posta kutusunda buradan yazılmamış bir filtre betiği var — büyük ihtimalle elle düzenlenmiş. Oluşturucu yalnızca okuyabildiğini gösterir ve kaydetmek betiğin tamamının yerine geçer. Önce aşağıdaki Sieve’i kontrol et.',
    'rules.inactive': 'Bu kuralların hiçbiri çalışmıyor. Sunucu başka bir betiği çalıştırıyor — “{name}”. Buradan kaydetmek bunu yeniden etkin yapar.',
    'rules.brokenOne': 'Bir kural artık var olmayan bir klasörü gösteriyor. Yenisini seçene kadar, eski addan yeniden oluşturulan bir klasöre dosyalar; bu da işe yaramış gibi görünür. Eksik: {list}.',
    'rules.brokenMany': '{count} kural artık var olmayan bir klasörü gösteriyor. Yenilerini seçene kadar, eski adlardan yeniden oluşturulan klasörlere dosyalarlar; bu da işe yaramış gibi görünür. Eksik: {list}.',
    'rules.connecting': 'bağlanılıyor ve kurallar yükleniyor…',
    'rules.cannotConnect': 'bu posta kutusuna bağlanılamadı',
    'rules.saveDisabled': 'Bu bitene kadar Kaydet kapalı kalır.',
    'rules.on': 'açık',
    'rules.delete': 'Sil',
    'rules.runEarlier': 'Daha önce çalışsın',
    'rules.runLater': 'Daha sonra çalışsın',
    'rules.if': 'eğer',
    'rules.matchAll': 'hepsi tutuyorsa',
    'rules.matchAny': 'herhangi biri tutuyorsa',
    'rules.addCondition': '+ koşul',
    'rules.then': 'o zaman',
    'rules.addAction': '+ işlem',
    'rules.stopAfter': 'bu kuraldan sonra dur',
    'rules.headerWarning': 'Yönlendirilen iletilere dikkat. Kimden, Kime ve Cc birer başlıktır ve yönlendirme bunları değiştirmez — hâlâ ilk göndereni ve ilk alıcıyı gösterirler. İletinin gerçekte nereye ulaştığını yakalamak için “Şuraya teslim edildi” ya da “Şuraya teslim edildi (+ kısmı)” kullan.',
    'rules.manyWildcards': 'burada birden fazla joker var — ${1} en son tutan jokeri gösterir, bu da genelde istediğin şey değildir. Kural başına tek joker tercih et.',
    'rules.wildcardHint': 'bu kural joker kullanıyor — * ile tutan kısmı kullanmak için klasör adına ${1} yaz. Klasör kendiliğinden oluşturulur.',
    'rules.add': '+ Kural ekle',
    'rules.showSieve': 'Sieve’i göster',
    'rules.hideSieve': 'Sieve’i gizle',
    'rules.save': 'Kuralları kaydet',
    'rules.saving': 'Kaydediliyor…',
    'rules.checking': 'Sunucuya soruluyor…',
    'rules.saved': 'Kaydedildi, sunucuda çalışıyor.',
    'rules.rejected': 'Sunucu bu kuralları kabul etmedi: {message}',
    'rules.saveTitle': 'Kuralları sunucuda doğrula ve kaydet',
    'rules.saveWaitTitle': 'Bu posta kutusuna hâlâ bağlanılıyor',
    'rules.footer': 'Bunlar ileti geldiği anda sunucuda çalışır; bu uygulama açık olsa da olmasa da işlerler. “Şuraya teslim edildi”, gerçek zarf adresini karşılaştırır — catch-all kurallarını mümkün kılan da budur. Engellenen göndericiler ve yönlendirme bunlardan önce çalışır.',
    'rules.folderGone': '{name} — klasör kayıp',
    'rules.patternOption': 'Kalıp ya da yeni klasör…',
    'rules.field.from': 'Kimden',
    'rules.field.to': 'Kime',
    'rules.field.cc': 'Cc',
    'rules.field.subject': 'Konu',
    'rules.field.recipient': 'Şuraya teslim edildi',
    'rules.field.detail': 'Şuraya teslim edildi (+ kısmı)',
    'rules.field.body': 'İleti metni',
    'rules.hint.header': 'başlıktır — yönlendirme bunu DEĞİŞTİRMEZ',
    'rules.hint.recipient': 'gerçek zarf adresi; catch-all ya da yönlendirmede bile',
    'rules.hint.detail': 'sen+birşey@alanadı adresindeki + işaretinden sonraki kısım',
    'rules.op.contains': 'içeriyorsa',
    'rules.op.is': 'tam olarak buysa',
    'rules.op.matches': 'kalıba uyuyorsa',
    'rules.op.regex': 'regex’e uyuyorsa',
    'rules.hint.matches': 'joker olarak * kullan — tuttuğu kısım ${1} olur',
    'rules.act.fileinto': 'Klasöre taşı',
    'rules.act.seen': 'Okundu işaretle',
    'rules.act.flagged': 'Yıldızla',
    'rules.act.forward': 'Şuraya ilet',
    'rules.act.discard': 'Sil',

    'tokens.surfaces': 'yüzeyler',
    'tokens.text': 'metin',
    'tokens.accent': 'vurgu rengi',
    'tokens.semantic': 'anlamsal',

    'theme.toggleToLight': 'Açık temaya geç',
    'theme.toggleToDark': 'Koyu temaya geç',
};
